import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { AlertCircle, Mail, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function EmailConfirmationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);

  // Don't show if user is confirmed, not logged in, or dismissed
  if (!user || user.email_confirmed || dismissed) return null;

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });
      
      if (error) throw error;
      toast.success('Confirmation email sent! Check your inbox.');
    } catch (err) {
      toast.error('Failed to resend email. Try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b-2 border-amber-200">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Please confirm your email address
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                You won't be able to add books or interact until you verify your email. Check your inbox for the confirmation link.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleResendEmail}
              disabled={resending}
              className="border-amber-300 text-amber-900 hover:bg-amber-100 text-xs h-8"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {resending ? 'Sending...' : 'Resend Email'}
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="text-amber-600 hover:text-amber-800 transition-colors p-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
